import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { NavLink, Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  IoSettingsSharp,
  IoBarcodeOutline,
  IoBarChartOutline,
} from "react-icons/io5";
import { AiOutlineProject, AiOutlineUser } from "react-icons/ai";

import { setUser } from "../redux/auth/actions";
import api from "../services/api";
import toast from "react-hot-toast";

const navigation = [
  { name: "Dashboard", href: "/", icon: AiOutlineProject },
  { name: "Users", href: "/user", icon: AiOutlineUser },
  { name: "Codes", href: "/code", icon: IoBarcodeOutline },
  // { name: "Reports", href: "/report", icon: IoBarChartOutline },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const handleLogout = async () => {
    try {
      const res = await api.post(`/users/logout`);
      if (!res.ok) {
        return toast.error(res.message);
      }
      dispatch(setUser(null));
      history.replace("/auth");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        {/* <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" /> */}
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white p-4">
                    <div className="flex h-12 shrink-0 items-center mx-auto">
                      <Logo />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <NavLink
                                  to={item.href}
                                  key={item.name}
                                  exact={item.name === "Dashboard"}
                                  activeClassName="bg-app/25 !text-app-gray font-medium"
                                  className="group flex items-center gap-x-2 rounded-md py-3 px-4 text-[#6E7581] text-sm font-medium transition-colors"
                                >
                                  <item.icon
                                    className={classNames("h-6 w-6 shrink-0")}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* desktop */}
        <div className="hidden z-50 lg:fixed lg:inset-y-0 lg:flex lg:w-48 lg:flex-col shadow-sidebar">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white p-4 border-r border-[#E8E8E8]">
            <div className="flex h-12 shrink-0 items-center mx-auto">
              <Logo />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <NavLink
                          to={item.href}
                          key={item.name}
                          exact={item.name === "Dashboard"}
                          activeClassName="bg-app/25 !text-app-gray font-medium"
                          className="group flex items-center gap-x-2 rounded-md py-3 px-4 text-[#6E7581] text-sm font-medium transition-colors"
                        >
                          <item.icon
                            className={classNames("h-6 w-6 shrink-0")}
                            aria-hidden="true"
                          />
                          {item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-48">
          <div className="sticky top-0 z-50 lg:px-6 border-b border-[#E8E8E8] bg-white">
            <div className="flex h-12 items-center gap-x-4 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                {/* <Bars3Icon className="h-6 w-6" aria-hidden="true" /> */}
              </button>

              {/* Separator */}
              <div
                className="h-6 w-px bg-gray-200 lg:hidden"
                aria-hidden="true"
              />

              <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 justify-end">
                <div className="flex items-center gap-x-4 lg:gap-x-6">
                  {/* Profile dropdown */}
                  <Menu as="div" className="relative">
                    <Menu.Button className="p-1.5 bg-white shadow-input rounded-lg">
                      <IoSettingsSharp size={18} color={"#718096"} />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-sidebar ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/account"
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "block px-3 py-1 text-sm leading-6 text-gray-900"
                              )}
                            >
                              My Account
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "block px-3 py-1 text-sm leading-6 text-gray-900 w-full text-left"
                              )}
                            >
                              Sign Out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
          <main className="p-6">{children}</main>
        </div>
      </div>
    </>
  );
}

const Logo = () => {
  return (
    <Link to="/" className="block">
      <span className="shrink-0 font-bold text-2xl">VizyGood</span>
    </Link>
  );
};
